import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  AssignmentTripLatenessRead,
  TripLatenessLeg,
} from 'src/common/external/bambi-api/bambiApi';

export type ItineraryStopsByTripLocationType = Partial<
  Record<'pickup' | 'dropoff', TripLatenessLeg>
>;

export interface ItineraryState {
  stopsByTripId: Record<
    string,
    Record<string, ItineraryStopsByTripLocationType | undefined> | undefined
  >;
  lateTripIdMap: Record<string, Record<string, boolean> | undefined>;
  // Cached organization settings that affect trip lateness
  organizationSettings: {
    ambulatory_load_seconds?: number;
    ambulatory_unload_seconds?: number;
    wheelchair_load_seconds?: number;
    wheelchair_unload_seconds?: number;
    wheelchair_xl_load_seconds?: number;
    wheelchair_xl_unload_seconds?: number;
    broda_chair_load_seconds?: number;
    broda_chair_unload_seconds?: number;
    geri_chair_load_seconds?: number;
    geri_chair_unload_seconds?: number;
    stretcher_load_seconds?: number;
    stretcher_unload_seconds?: number;
  };
}

export const initialState: ItineraryState = {
  stopsByTripId: {},
  lateTripIdMap: {},
  organizationSettings: {},
};

// `tripLateness.trips` contains every trip on the itinerary, not just late
// ones, so lateness has to be derived from the individual legs.
export function getLateTripIds(
  tripLateness: AssignmentTripLatenessRead | null | undefined
) {
  return Object.entries<ItineraryStopsByTripLocationType>(
    tripLateness?.trips ?? {}
  )
    .filter(([, legs]) =>
      Boolean(legs.pickup?.is_late || legs.dropoff?.is_late)
    )
    .map(([tripId]) => tripId);
}

export const itinerarySlice = createSlice({
  name: 'itinerary',
  initialState,
  reducers: {
    reset(state) {
      state.stopsByTripId = initialState.stopsByTripId;
      state.lateTripIdMap = initialState.lateTripIdMap;
    },
    cacheTripItineraryLateness(
      state,
      action: PayloadAction<{
        tripLateness: AssignmentTripLatenessRead | null | undefined;
        assignmentId: string;
      }>
    ) {
      const { tripLateness, assignmentId } = action.payload;
      if (!tripLateness) {
        state.stopsByTripId[assignmentId] = {};
        state.lateTripIdMap[assignmentId] = {};
        return;
      }

      const lateTrips = getLateTripIds(tripLateness);

      state.stopsByTripId[assignmentId] = tripLateness.trips;
      state.lateTripIdMap = {
        ...state.lateTripIdMap,
        [assignmentId]: lateTrips.reduce<Record<string, boolean>>(
          (acc, id) => ({
            [id]: true,
            ...acc,
          }),
          {}
        ),
      };
    },
    cacheOrganizationSettings(
      state,
      action: PayloadAction<ItineraryState['organizationSettings']>
    ) {
      state.organizationSettings = action.payload;
    },
  },
  selectors: {
    selectStopsByTripId(state, assignmentId: string, tripId: string) {
      return state.stopsByTripId[assignmentId]?.[tripId];
    },
    selectIsTripLate(state, assignmentId: string, tripId: string) {
      const trips = state.lateTripIdMap[assignmentId];
      return Boolean(trips?.[tripId]);
    },
  },
});

export const { cacheTripItineraryLateness, reset, cacheOrganizationSettings } =
  itinerarySlice.actions;
export const { selectStopsByTripId, selectIsTripLate } =
  itinerarySlice.selectors;
